import { applyPriceCut, getDiscountInfo, type PriceCut } from "@/lib/pricing";
import { discountBadgeText } from "@/lib/promo";
import { orderItemBundleDiscountPerPcs } from "@/lib/bundleOrders";

// ─── HARGA PER-VARIAN DI BARIS KERANJANG ─────────────────────────────────────
//
// Dipakai baris varian keranjang pembeli (`/orders`) DAN keranjang admin
// per-reseller (`/admin/orders/keranjang/[customerId]`) lewat komponen bersama
// `CartVariantRow`. Satu definisi supaya dua layar tak pernah menghitung harga
// yang sama dengan aturan sendiri.
//
// ANGKA YANG DITAMPILKAN = `order_items.unit_price`, SNAPSHOT SAAT MEMESAN —
// bukan hasil hitung ulang harga level hari ini. Alasannya sama dengan yang
// ditulis panjang di `src/lib/levelPrice.ts`: `unit_price` adalah kolom yang
// BENAR-BENAR ditagih, dan sebagian pembacanya ada di SERVER dan tak bisa
// dikoreksi dari klien (`admin_mark_items_paid` menghitung nominal Tandai Lunas
// sendiri dari snapshot, `compute_order_dp_total` menghitung DP dari snapshot,
// laporan/omzet ikut membacanya). Menampilkan harga level hari ini di baris
// keranjang berarti menjanjikan angka yang tak akan ditagih — dua harga berbeda
// untuk barang yang sama di dua layar. Snapshot yang terlanjur harga penuh
// diperbaiki lewat banner "Perbaiki Harga" (levelPrice.ts), yang MENULIS
// snapshotnya, bukan menambal tampilan.
//
// HARGA NORMAL (yang dicoret) HANYA dari `product_variants.price_normal` lewat
// `variant_id`. Dua lapis lain milik resolver kanonik DB
// `resolve_order_item_normal_price` (mig 0952) SENGAJA tidak diikuti:
//
//   · "cari varian by NAMA" menuntut menarik SELURUH varian produknya, dan
//     produk PO terbesar di PROD memegang 4.644 kombinasi — audit performa
//     mig 210 justru membuang embed semacam itu dari jalur keranjang;
//   · `products.price_normal` DILARANG dipakai sebagai angka coret. ProductForm
//     menyimpannya sebagai MIN harga terefektif SELURUH varian
//     (`ProductForm.tsx` "Harga produk = terendah dari harga TEREFEKTIF seluruh
//     varian"), jadi pada produk ber-harga campuran ia LEBIH MURAH dari harga
//     jual varian yang sedang dilihat. Larangan yang sama sudah ditulis di
//     `pricing.ts:245-255` untuk kartu katalog. Memakainya di sini bukan cuma
//     menghilangkan coretan — pada kasus snapshot di bawah MIN ia akan MENCETAK
//     angka coret dan persen yang SALAH di kolom uang (varian normal 100rb,
//     varian lain 20rb → `price_normal` 20rb; snapshot 15rb → tampil "−25%"
//     padahal sebenarnya −85%).
//
// Konsekuensinya: item ber-`variant_id` NULL/basi, varian yang sudah dihapus,
// dan varian yang TAK TERBACA RLS tampil TANPA coretan — "tidak tahu", bukan
// "tidak ada diskon". Harganya tetap tampil. Itu arah kegagalan yang benar.
//
// CATATAN RLS (sisi pembeli). Policy "Public can read product variants"
// (mig 0010) menuntut `product_variants.is_active` DAN `products.is_active`.
// Mig 1230 dulu menambahkan policy "pembeli boleh membaca produk yang
// dipesannya walau nonaktif" — tapi HANYA untuk `products`, tak pernah
// dicerminkan ke `product_variants`. Jadi untuk produk manual (mig 121) dan
// ready stock yang auto-nonaktif saat stok habis (mig 119), pembeli tak
// mendapat coretan sementara admin mendapatkannya. Aman (harga tetap benar),
// tapi memang tak seragam — memperbaikinya butuh migrasi RLS tersendiri.
//
// CATATAN SNAPSHOT vs HARI INI. Nota memakai `invoice_lines.unit_normal_price`
// (snapshot saat nota terbit, mig 0952); baris keranjang ini memakai
// `price_normal` HARI INI. Begitu harga normal produk diubah, angka coret di
// keranjang dan di nota bisa berbeda. Disengaja: keranjang belum punya nota,
// jadi tak ada snapshot yang bisa dibaca — dan angka yang DITAGIH
// (`unit_price`) tetap sama di kedua layar.

/** Harga satu baris varian, siap dirender. */
export type CartPriceView = {
  /** Harga satuan yang ditagih (snapshot `order_items.unit_price`). */
  unitPrice: number;
  /**
   * Harga tambahan varian (mig 7900) yang TERMASUK di `unitPrice` — potret
   * `order_items.unit_price_extra`; 0 = tanpa tambahan. Potongan & persen di
   * bawah dinilai atas `unitPrice − priceExtra` (tambahan di luar diskon),
   * sedangkan `normalPrice` (coretan) sudah memuat tambahan supaya coretan
   * dan harga yang dibayar sama-sama "harga utuh".
   */
  priceExtra: number;
  /**
   * Harga untuk DICORET. `null` = tak diketahui, atau tidak lebih mahal
   * dari `unitPrice` (pembeli tanpa level, atau snapshot yang justru di atas
   * harga normal) — dua-duanya berarti tak ada yang layak dicoret.
   *
   * Sejak jejak potongan (mig promo_harga_coret): pada baris promo harga coret
   * yang juga dipotong level, angka ini = HARGA CORET (harga umum − promo),
   * cermin halaman produk (keputusan pemilik 2026-09-16) — bukan harga umum.
   */
  normalPrice: number | null;
  /** Persen potongan TOTAL dari harga umum, selalu sejalan dengan coretan. */
  discountPercent: number | null;
  /**
   * Potongan yang membentuk `unitPrice`, urut penerapan — dari jejak
   * `order_items.promo_/level_discount_*`. Kosong = pesanan sebelum jejak ada
   * (tampilan jatuh ke persen selisih seperti dulu).
   */
  cuts: readonly PriceCut[];
  /** Teks pil ("🔥 10% + 30%", "🔥 -10%", "-30%"); `null` bila tak ada potongan. */
  badge: string | null;
  /** Rupiah yang dihemat per pcs terhadap harga umum; `null` bila tak diketahui. */
  savingsPerPcs: number | null;
  /** `unitPrice × qty`. */
  subtotal: number;
  /**
   * Bagian potongan BUNDEL per pcs (mig 8040) — langkah TERAKHIR rantai:
   * `cuts` menjelaskan harga terpisah (`bundle_base_unit_price`), lalu bundel.
   * Kunci hanya ada pada baris bundel (> 0); baris biasa tak membawanya.
   */
  bundleRp?: number;
  /**
   * Baris HADIAH pembelian (mig 8680): Rp 0 yang sah, ditampilkan "Gratis" —
   * bukan potongan 100 %. Kunci hanya ada pada baris hadiah.
   */
  isGift?: true;
};

/** Kolom jejak potongan `order_items` (mig promo_harga_coret) apa adanya dari DB. */
export type DiscountTraceRow = {
  promo_discount_mode?: string | null;
  promo_discount_value?: number | string | null;
  level_discount_mode?: string | null;
  level_discount_value?: number | string | null;
};

/**
 * Baris `order_items` mentah (PostgREST) → jejak potongan; `null` bila
 * keempat kolomnya kosong (pesanan lama / baris tanpa jejak) supaya pemakai
 * bisa membedakan "tak tercatat" dari "tercatat tanpa potongan".
 */
export function discountTraceFromRow(
  row: Record<string, unknown> | null | undefined
): DiscountTraceRow | null {
  if (!row) return null;
  const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "string" ? Number(v) : (v as number);
    return Number.isFinite(n) ? n : null;
  };
  const trace: DiscountTraceRow = {
    promo_discount_mode: (row.promo_discount_mode as string | null | undefined) ?? null,
    promo_discount_value: num(row.promo_discount_value),
    level_discount_mode: (row.level_discount_mode as string | null | undefined) ?? null,
    level_discount_value: num(row.level_discount_value)
  };
  return trace.promo_discount_value === null && trace.level_discount_value === null ? null : trace;
}

/**
 * Diskon MANUAL satu baris (`order_items.manual_discount_mode/_value`, mig 7060
 * — Ubah Diskon keranjang admin): persen dari Harga Jual baris, atau rupiah per
 * pcs. Menggantikan potongan otomatis (level/grup) baris itu.
 */
export type ItemManualDiscount = { mode: "persen" | "nominal"; value: number };

/**
 * Baris `order_items` mentah → diskon manual; `null` bila kosong ATAU tak sah
 * (mode asing / nilai tak terbaca) — CHECK DB menolak keduanya, tapi pembaca
 * tak boleh bergantung padanya (idiom `cutsFromTrace`).
 */
export function manualDiscountFromRow(
  row: Record<string, unknown> | null | undefined
): ItemManualDiscount | null {
  if (!row) return null;
  const mode = row.manual_discount_mode;
  if (mode !== "persen" && mode !== "nominal") return null;
  const raw = row.manual_discount_value;
  if (raw === null || raw === undefined || raw === "") return null;
  const value = typeof raw === "string" ? Number(raw) : (raw as number);
  if (!Number.isFinite(value) || value < 0) return null;
  return { mode, value };
}

/**
 * Diskon manual sebagai JEJAK potongan (satu potongan "level") — supaya badge
 * keranjang & teks Rekap Barang menjelaskan baris itu PERSIS dengan diskon yang
 * diketik admin, bukan menebak dari selisih harga. Server mengosongkan jejak
 * promo/level saat diskon manual dipasang (mig 7060), jadi tak ada yang tertimpa.
 */
export function manualDiscountAsTrace(d: ItemManualDiscount): DiscountTraceRow {
  return {
    promo_discount_mode: null,
    promo_discount_value: null,
    level_discount_mode: d.mode,
    level_discount_value: d.value
  };
}

/**
 * Jejak potongan yang DITAMPILKAN untuk satu baris: diskon manual menang atas
 * jejak tersimpan (yang memang dikosongkan server saat diskon manual dipasang),
 * supaya badge menyebut diskon PERSIS seperti yang diketik admin. Satu aturan
 * untuk keranjang admin, Rekap Barang, dan keranjang pembeli (bughunt D12-3).
 */
export function displayDiscountTrace(item: {
  discountTrace: DiscountTraceRow | null;
  manualDiscount?: ItemManualDiscount | null;
}): DiscountTraceRow | null {
  return item.manualDiscount ? manualDiscountAsTrace(item.manualDiscount) : item.discountTrace;
}

/**
 * Jejak potongan baris → `cuts` urut penerapan (promo dulu, lalu level).
 * Nilai tak terbaca / ≤ 0 dilewati; mode tak dikenal dianggap persen — sama
 * dengan `mapPromoRow`. Baris tanpa jejak (pesanan lama) → kosong.
 */
export function cutsFromTrace(row: DiscountTraceRow | null | undefined): PriceCut[] {
  if (!row) return [];
  const out: PriceCut[] = [];
  const push = (source: PriceCut["source"], mode: string | null | undefined, raw: number | string | null | undefined) => {
    const value = typeof raw === "string" ? Number(raw) : raw;
    if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) return;
    // Mode di luar enum (termasuk NULL dengan nilai terisi) BUKAN potongan —
    // dulu jatuh ke "persen" dan mengarang badge dari jejak yang setengah
    // tercatat (audit pasca-rilis 2026-09-17); CHECK DB memang menolak mode
    // asing, tapi pembaca tak boleh bergantung padanya.
    if (mode !== "persen" && mode !== "nominal") return;
    out.push({ source, mode, value });
  };
  push("promo", row.promo_discount_mode, row.promo_discount_value);
  // Jejak tak membedakan override varian dari diskon grup — keduanya "level"
  // di layar pasca-pesan; `group` cukup untuk teks badge.
  push("group", row.level_discount_mode, row.level_discount_value);
  return out;
}

/**
 * Bentuk `CartPriceView` untuk satu item keranjang.
 *
 * `null` bila harganya belum ada (`unit_price` NULL/0 = produk "harga
 * menyusul") — pemanggil lalu TIDAK merender apa pun. Sengaja bukan
 * `formatPrice(undefined)`: helper itu memulangkan kalimat "Harga belum
 * tersedia" yang jauh lebih panjang dari nama varian dan merusak baris sempit
 * di HP.
 *
 * Keputusan "boleh dicoret" didelegasikan ke `getDiscountInfo` (pricing.ts) —
 * aturan yang sama dengan kartu produk & halaman produk. Kolom uang tak boleh
 * punya dua definisi "sedang diskon" yang bisa menyimpang.
 */
export function cartItemPriceView(item: {
  qty: number;
  unitPrice: number | null | undefined;
  variantId: string | null | undefined;
  /** variantId → `product_variants.price_normal`. Absen = tidak tahu. */
  normalByVariant: ReadonlyMap<string, number>;
  /**
   * Harga BARIS produk Non Web (`order_items.manual_price_normal`, mig 6390)
   * — MENANG atas harga varian: pada SKU bersama Harga per reseller SAH
   * (bughunt D8-2(b)) dan varian hanya memegang Harga reseller pertama.
   * Absen/null = harga varian seperti biasa.
   */
  rowNormalPrice?: number | null;
  /**
   * Baris ini memakai voucher (`order_items.voucher_name`). Sejak plafon persen
   * boleh kosong (mig 5660), voucher 100 % — atau nominal ≥ harga baris —
   * menghasilkan `unit_price = 0` yang SAH; tanpa penanda ini baris gratis
   * dipajang "Harga belum tersedia" seperti produk harga-menyusul
   * (audit 2026-09-10).
   */
  hasVoucher?: boolean;
  /** Jejak potongan baris (promo/level) — sumber `cuts` & badge. Absen = tak tercatat. */
  trace?: DiscountTraceRow | null;
  /**
   * Potret tambahan baris (`order_items.unit_price_extra`, mig 7900) — bagian
   * `unitPrice` yang di luar diskon. Absen/null = 0 (baris pra-7900).
   */
  priceExtra?: number | null;
  /**
   * BUNDEL (mig 8040): `order_items.bundle_base_unit_price` (harga terpisah per
   * pcs termasuk tambahan, tanpa voucher), `bundle_base_unit_price_after_voucher`
   * (harga terpisah sesudah voucher; null = baris tak ber-voucher) + voucher per
   * pcs baris itu. Absen / null = baris biasa → hasil persis seperti sebelumnya.
   */
  bundleBaseUnitPrice?: number | null;
  bundleBaseUnitPriceAfterVoucher?: number | null;
  voucherPerPcs?: number | null;
  /** Baris HADIAH pembelian (`order_items.gift_role = 'hadiah'`, mig 8680). */
  isGift?: boolean;
}): CartPriceView | null {
  const unitPrice = Number(item.unitPrice ?? 0);
  if (!Number.isFinite(unitPrice)) return null;
  if (unitPrice < 0) return null;
  if (item.isGift && unitPrice === 0) {
    // Hadiah Rp 0: tanpa coretan & tanpa pil potongan (rencana §4.1).
    return {
      unitPrice: 0,
      priceExtra: 0,
      normalPrice: null,
      discountPercent: null,
      cuts: [],
      badge: null,
      savingsPerPcs: null,
      subtotal: 0,
      isGift: true
    };
  }
  if (unitPrice === 0 && !item.hasVoucher) return null;

  const qty = Math.max(0, Math.floor(Number(item.qty) || 0));
  // Tambahan tak pernah melebihi harga yang ditagih (lint `price_extra_lint`);
  // kalau datanya janggal, dinilai 0 supaya tak melahirkan diskon palsu.
  const rawExtra = Number(item.priceExtra ?? 0);
  const priceExtra = Number.isFinite(rawExtra) && rawExtra > 0 && rawExtra <= unitPrice ? rawExtra : 0;
  // Harga DASAR yang ditagih = bagian yang lahir dari harga umum − potongan.
  const unitBase = unitPrice - priceExtra;

  const rowNormal =
    item.rowNormalPrice != null && Number.isFinite(Number(item.rowNormalPrice))
      ? Number(item.rowNormalPrice)
      : undefined;
  const rawNormal =
    rowNormal ?? (item.variantId ? item.normalByVariant.get(item.variantId) : undefined);
  const normal = rawNormal === undefined ? undefined : Number(rawNormal);
  const knownNormal = normal !== undefined && Number.isFinite(normal) ? normal : undefined;

  // Diskon dinilai atas harga DASAR (tambahan di luar diskon, mig 7900):
  // umum 100.000 → ditagih 110.000 (= 90.000 + tambahan 20.000) tetap "-10%".
  const discount = getDiscountInfo(knownNormal, unitBase);
  // Jejak hanya DIPAKAI bila harga umum yang tercoret memang lebih mahal dari
  // yang ditagih; kalau tidak (harga umum tak diketahui / snapshot di atas
  // normal) tak ada potongan yang bisa dijelaskan — sama seperti sebelumnya.
  const cuts = discount ? cutsFromTrace(item.trace) : [];
  // Coretan = harga sebelum potongan TERAKHIR: dua potongan → harga coret
  // (umum − promo); satu potongan / tanpa jejak → harga umum. Lalu tambahan
  // ditempel lagi: coretan dan harga bayar sama-sama harga utuh.
  const strikeBase =
    discount && cuts.length >= 2 ? applyPriceCut(discount.normalPrice, cuts[0]) : discount?.normalPrice ?? null;
  const strike = strikeBase !== null && strikeBase > unitBase ? strikeBase : discount?.normalPrice ?? null;

  // BUNDEL (mig 8040): jejak potongan menjelaskan harga TERPISAH, bukan harga
  // bundel — badge "-5%" yang berdiri sendiri di samping harga bundel akan
  // mengklaim potongan yang salah. Rantainya: jejak (atau selisih umum →
  // terpisah) lalu "Bundel" (= terpisah sesudah voucher − bayar).
  const bundleRp = orderItemBundleDiscountPerPcs({
    bundleBaseUnitPrice: item.bundleBaseUnitPrice ?? null,
    bundleBaseUnitPriceAfterVoucher: item.bundleBaseUnitPriceAfterVoucher ?? null,
    unitPrice,
    voucherDiscountPerPcs: item.voucherPerPcs ?? null
  });
  if (discount && bundleRp !== null && bundleRp > 0) {
    const separateBase = Number(item.bundleBaseUnitPrice) - priceExtra;
    const lead =
      discountBadgeText(cuts, { flame: true }) ??
      (separateBase < discount.normalPrice
        ? `-${Math.round(((discount.normalPrice - separateBase) / discount.normalPrice) * 100)}%`
        : null);
    return {
      unitPrice,
      priceExtra,
      normalPrice: strike === null ? null : strike + priceExtra,
      discountPercent: discount.percent,
      cuts,
      badge: lead ? `${lead} + Bundling` : "Bundling",
      savingsPerPcs: Math.max(0, discount.normalPrice - unitBase),
      subtotal: unitPrice * qty,
      bundleRp
    };
  }

  return {
    unitPrice,
    priceExtra,
    normalPrice: strike === null ? null : strike + priceExtra,
    discountPercent: discount?.percent ?? null,
    cuts,
    badge: discount ? discountBadgeText(cuts, { flame: true }) ?? `-${discount.percent}%` : null,
    savingsPerPcs: discount ? Math.max(0, discount.normalPrice - unitBase) : null,
    subtotal: unitPrice * qty
  };
}
